import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Building2, MessageSquare, Star } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import axiosInstance from "../../services/api/axios";
import { getPresignedDownloadUrl } from "../../utils/fileUtils";
import ChatPanel from "../../components/marketplace/ChatPanel";
import DeliverableThread from "../../components/marketplace/DeliverableThread";
import StarRating from "../../components/StarRating";
import {
  getProject,
  listDeliverables,
  reviewDeliverable,
  submitFeedback,
} from "../../services/archMarketplace";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const humanize = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

async function openKey(key) {
  try {
    const res = await getPresignedDownloadUrl(key, axiosInstance);
    const url = res?.download_url || res?.downloadUrl;
    if (url) window.open(url, "_blank");
  } catch {
    toast.error("Could not open file.");
  }
}

function ArchitectProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackText, setFeedbackText] = useState("");
  const [rating, setRating] = useState(0);
  const [changeNote, setChangeNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [p, d] = await Promise.all([getProject(projectId), listDeliverables(projectId).catch(() => [])]);
    setProject(p);
    setDeliverables(Array.isArray(d) ? d : []);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await refresh();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const latest = deliverables[deliverables.length - 1];

  const handleReview = async (action) => {
    if (action === "REQUEST_CHANGES" && !changeNote.trim()) {
      toast.error("Please describe the changes needed.");
      return;
    }
    setBusy(true);
    try {
      await reviewDeliverable(projectId, action, action === "REQUEST_CHANGES" ? changeNote.trim() : null);
      toast.success(action === "ACCEPT" ? "Project completed!" : "Changes requested.");
      setChangeNote("");
      await refresh();
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Review failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleFeedback = async () => {
    if (!rating) {
      toast.error("Please pick a rating.");
      return;
    }
    setBusy(true);
    try {
      await submitFeedback(projectId, rating, feedbackText.trim() || null);
      toast.success("Thanks for your feedback!");
      await refresh();
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Could not submit feedback.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !project) return <div className="p-6 text-gray-400">Loading…</div>;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={project.projectName}
        subtitle={`${humanize(project.status)} · ${fmtINR(project.approvedPrice ?? project.estimatedPrice)}`}
        icon={Building2}
        backTo="/architect/dashboard"
        backLabel="Back to projects"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Overview */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{project.description}</p>
            {project.status === "ROUTING" && (
              <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                We're reaching out to matched specialists one at a time. You'll be notified when someone accepts.
              </p>
            )}
            {project.status === "NO_CANDIDATES" && (
              <p className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
                No specialist was available — the held amount was released back to your wallet. Please try again shortly.
              </p>
            )}
          </div>

          {/* Deliverables */}
          {deliverables.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Deliverables &amp; Revisions
              </h3>
              <DeliverableThread deliverables={deliverables} perspective="ARCHITECT" onOpen={openKey} />

              {/* Review actions for the latest, when in review — two clearly separate paths. */}
              {project.status === "IN_REVIEW" && latest && (
                <div className="mt-4 border-t pt-4 space-y-4">
                  {/* Request changes — its own feedback box + button */}
                  <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-4">
                    <h4 className="text-sm font-semibold text-orange-800">Need revisions?</h4>
                    <p className="mt-0.5 text-xs text-orange-700">
                      Describe what to improve — the freelancer will deliver a new version.
                    </p>
                    <textarea
                      rows={2}
                      value={changeNote}
                      onChange={(e) => setChangeNote(e.target.value)}
                      placeholder="e.g. Add the staircase section detail and fix the north arrow…"
                      className="mt-2 w-full rounded-md border border-orange-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    />
                    <button
                      onClick={() => handleReview("REQUEST_CHANGES")}
                      disabled={busy || !changeNote.trim()}
                      className="mt-2 rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                    >
                      Send change request
                    </button>
                  </div>

                  {/* Accept — separate, guarded action */}
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                    <h4 className="text-sm font-semibold text-emerald-800">Happy with the design?</h4>
                    <p className="mt-0.5 text-xs text-emerald-700">
                      Accepting marks the project complete and captures payment. This can't be undone.
                    </p>
                    <button
                      onClick={() => {
                        if (window.confirm("Accept this design and mark the project complete? This can't be undone.")) {
                          handleReview("ACCEPT");
                        }
                      }}
                      disabled={busy}
                      className="mt-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Accept &amp; Complete
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Feedback on completion */}
          {project.status === "COMPLETED" && (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                <Star size={16} /> Rate this project
              </h3>
              <StarRating value={rating} onChange={setRating} />
              <textarea
                rows={2}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Optional comment…"
                className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-300"
              />
              <button
                onClick={handleFeedback}
                disabled={busy}
                className="mt-3 rounded-md bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50"
              >
                Submit Feedback
              </button>
            </div>
          )}
        </div>

        {/* Chat */}
        <div className="space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-600">
            <MessageSquare size={16} /> Q&amp;A
          </h3>
          <ChatPanel
            projectId={projectId}
            myEntity="ARCHITECT"
            disabled={!["ASSIGNED", "IN_PROGRESS", "IN_REVIEW", "CHANGES_REQUESTED", "COMPLETED"].includes(project.status)}
          />
        </div>
      </div>
    </div>
  );
}

export default ArchitectProjectDetail;
